import { useState } from 'react';
import { ArrowLeft, CheckSquare, Trophy, Gift } from 'lucide-react';

export default function LeadForm({ flow, score, onSubmit, onBack }) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [rgpd, setRgpd] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const newErrors = {};
    if (!name.trim()) newErrors.name = 'Nome obrigatório';
    const phoneClean = phone.replace(/\s+/g, '').replace('+351', '');
    if (!/^\d{9}$/.test(phoneClean)) newErrors.phone = 'Telemóvel inválido (9 dígitos)';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) newErrors.email = 'Email inválido';
    if (!rgpd) newErrors.rgpd = 'Autorização obrigatória';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    const phoneClean = phone.replace(/\s+/g, '').replace('+351', '');
    onSubmit({ name: name.trim(), phone: phoneClean, email: email.trim() });
  };

  const isQuiz = flow === 'quiz';
  const title = isQuiz ? 'Entrar no Sorteio' : 'Foto IA no Kart';
  const buttonText = isQuiz ? 'Confirmar Participação' : 'Continuar';

  return (
    <div className="px-6 py-8 animate-fade-in" data-testid="lead-form-screen">
      {/* Back Button */}
      <button
        onClick={onBack}
        className="flex items-center gap-2 mb-6 transition-colors"
        style={{ color: '#A3A3A3' }}
        data-testid="lead-form-back-btn"
      >
        <ArrowLeft size={18} strokeWidth={1.5} />
        <span className="font-body text-sm">Voltar</span>
      </button>

      {/* Animated score banner (quiz only) */}
      {isQuiz && (
        <div className="text-center mb-6 animate-fade-in-up" style={{ animationDelay: '100ms' }}>
          <div
            className="mx-auto mb-3 w-16 h-16 rounded-full flex items-center justify-center animate-pulse-gold"
            style={{
              background: 'rgba(181, 152, 90, 0.12)',
              border: '2px solid #B5985A',
            }}
          >
            <Trophy size={28} strokeWidth={1.5} style={{ color: '#B5985A' }} />
          </div>
          <h2
            className="font-headline text-2xl text-white mb-1"
            data-testid="lead-form-score"
          >
            Acertou {score} de 5!
          </h2>
        </div>
      )}

      {/* Benefit card (quiz only) */}
      {isQuiz && (
        <div
          className="rounded-xl p-4 mb-6 animate-fade-in-up"
          style={{
            background: 'rgba(181, 152, 90, 0.08)',
            border: '1px solid rgba(181, 152, 90, 0.2)',
            animationDelay: '250ms',
            opacity: 0,
          }}
        >
          <div className="flex items-start gap-3">
            <Gift size={20} strokeWidth={1.5} style={{ color: '#B5985A', flexShrink: 0, marginTop: 2 }} />
            <div>
              <p className="font-subtitle text-sm text-white mb-1">
                Preencha os dados e entre no sorteio!
              </p>
              <p className="font-body text-xs" style={{ color: '#A3A3A3' }}>
                Todos os trimestres sorteamos uma volta de kart no Kartódromo Internacional de Palmela entre todos os participantes do quiz.
              </p>
            </div>
          </div>
        </div>
      )}

      {!isQuiz && (
        <>
          <h2
            className="font-headline text-xl mb-2 text-white"
            data-testid="lead-form-title"
          >
            {title}
          </h2>
          <p className="font-body text-sm mb-6" style={{ color: '#A3A3A3' }}>
            Preencha os seus dados para continuar
          </p>
        </>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Name */}
        <div>
          <label className="font-subtitle text-xs mb-1 block" style={{ color: '#B5985A', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
            Nome completo
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="O seu nome"
            className={`input-c21 ${errors.name ? 'error' : ''}`}
            data-testid="lead-form-name"
          />
          {errors.name && (
            <p className="text-xs mt-1" style={{ color: 'var(--c21-error)' }} data-testid="lead-form-name-error">{errors.name}</p>
          )}
        </div>

        {/* Phone */}
        <div>
          <label className="font-subtitle text-xs mb-1 block" style={{ color: '#B5985A', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
            Telemóvel
          </label>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+351 9XX XXX XXX"
            className={`input-c21 ${errors.phone ? 'error' : ''}`}
            data-testid="lead-form-phone"
          />
          {errors.phone && (
            <p className="text-xs mt-1" style={{ color: 'var(--c21-error)' }} data-testid="lead-form-phone-error">{errors.phone}</p>
          )}
        </div>

        {/* Email */}
        <div>
          <label className="font-subtitle text-xs mb-1 block" style={{ color: '#B5985A', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
            Email
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="email@exemplo.pt"
            className={`input-c21 ${errors.email ? 'error' : ''}`}
            data-testid="lead-form-email"
          />
          {errors.email && (
            <p className="text-xs mt-1" style={{ color: 'var(--c21-error)' }} data-testid="lead-form-email-error">{errors.email}</p>
          )}
        </div>

        {/* RGPD */}
        <div>
          <label className="flex items-start gap-3 cursor-pointer" data-testid="lead-form-rgpd-label">
            <div className="pt-0.5">
              <div
                onClick={() => setRgpd(!rgpd)}
                className="w-5 h-5 rounded flex-shrink-0 flex items-center justify-center transition-colors"
                style={{
                  border: `1.5px solid ${errors.rgpd ? 'var(--c21-error)' : rgpd ? '#B5985A' : 'rgba(255,255,255,0.2)'}`,
                  background: rgpd ? '#B5985A' : 'transparent',
                }}
                data-testid="lead-form-rgpd-checkbox"
                role="checkbox"
                aria-checked={rgpd}
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); setRgpd(!rgpd); }}}
              >
                {rgpd && <CheckSquare size={14} style={{ color: '#1A1A1A' }} />}
              </div>
            </div>
            <span className="font-body text-xs" style={{ color: '#A3A3A3', lineHeight: 1.5 }}>
              Autorizo o contacto por parte de {' '}
              <span style={{ color: '#B5985A' }}>Júlio Amaral — Century 21</span>
              {' '} para fins comerciais.
            </span>
          </label>
          {errors.rgpd && (
            <p className="text-xs mt-1" style={{ color: 'var(--c21-error)' }} data-testid="lead-form-rgpd-error">{errors.rgpd}</p>
          )}
        </div>

        {/* Submit */}
        <button
          type="submit"
          className="btn-gold w-full mt-4"
          data-testid="lead-form-submit-btn"
        >
          {buttonText}
        </button>
      </form>
    </div>
  );
}
